import { __decorate, __metadata } from "tslib";
import IdleTracker from 'idle-tracker';
import { logActivity, logger, logVerboseDecorator } from './logger';
import { browserActivityEvents, TRACK_CONTAINER_EVENT_NAME } from './constants';
import { connectContainer, disconnectContainer } from './utility';
import { ActivityEventType } from '@metaos/mfs-telemetry';
/**
 * Implementation of MfsIdleTracker for Browser environments. Automatically starts tracking user idleness when the first
 * Fluid container is added to this tracker.
 */
export class BrowserIdleTracker {
    /**
     * BrowserIdleTracker constructor
     *
     * @param config Configuration for this BrowserIdleTracker object
     */
    constructor(config) {
        this.containerSet = new Set();
        this.isEnabled = config.isEnabled;
        this.idleTracker = new IdleTracker({
            timeout: config.idleTimeoutMs,
            onIdleCallback: (callbackPayload) => this.onIdleCallback(callbackPayload),
            events: browserActivityEvents,
            throttle: 0, // Setting throttle to 0 disables IdleTracker throttle feature that is not needed in MFS
        });
    }
    /**
     * Accepts a new configuration and restarts the tracker if the configuration changed
     *
     * @param config New configuration for the tracker
     */
    changeConfiguration(config) {
        const isConfigurationModified = this.isEnabled !== config.isEnabled || this.idleTracker.timeout !== config.idleTimeoutMs;
        if (!config.isEnabled && this.isEnabled !== config.isEnabled) {
            // If we transitioned from enabled to disabled state, we need to reconnect all disconnected containers, otherwise
            // we run the risk of the containers being disconnected forever because the idle tracker won't reconnect them when
            // there is user activity.
            this.containerSet.forEach((container) => {
                connectContainer(container);
            });
        }
        if (isConfigurationModified) {
            this.idleTracker.end();
            this.isEnabled = config.isEnabled;
            this.idleTracker.timeout = config.idleTimeoutMs;
            if (this.isEnabled && this.containerSet.size > 0) {
                this.idleTracker.start();
            }
        }
    }
    /** @inheritDoc */
    track(container) {
        this.containerSet.add(container);
        if (!this.isStarted() && this.isEnabled) {
            this.idleTracker.start();
        }
        document.dispatchEvent(new Event(TRACK_CONTAINER_EVENT_NAME));
    }
    /**
     * Checks if this tracker has been started
     *
     * @returns A boolean value indicating if this tracker has started
     */
    isStarted() {
        return this.idleTracker.timer !== null;
    }
    /** @inheritDoc */
    dispose() {
        this.idleTracker.end();
        this.containerSet.clear();
    }
    /**
     * Event callback executed when user transitions to idle or active state.
     *
     * @param callbackPayload Payload as defined by IdleTracker library
     */
    onIdleCallback(callbackPayload) {
        const isIdle = callbackPayload.idle;
        if (isIdle) {
            logger.logInfo('userIdle', 'User became idle', 'onIdleCallback');
        }
        else {
            logger.logInfo('userActive', 'User became active', 'onIdleCallback');
        }
        this.containerSet.forEach((container) => {
            if (isIdle) {
                this.disconnectContainerOrSchedule(container);
            }
            else {
                connectContainer(container);
            }
        });
    }
    /**
     * Disconnects container if it can be disconnected right away, or schedule for disconnection if in dirty state
     *
     * @param container Container to disconnect
     */
    disconnectContainerOrSchedule(container) {
        if (container.isDirty) {
            logger.logInfo('scheduleDirtyContainerDisconnect', 'Scheduling dirty container for disconnection', 'disconnectContainerOrSchedule');
            // Schedule disconnection for when the container data has been saved and no longer dirty
            container.once('saved', () => {
                // This event listener will be executed sometime in the future. At that point, the user might not be
                // idle anymore, so we check the user state before disconnecting.
                if (this.idleTracker.isIdle()) {
                    disconnectContainer(container);
                }
            });
        }
        else {
            disconnectContainer(container);
        }
    }
}
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], BrowserIdleTracker.prototype, "changeConfiguration", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], BrowserIdleTracker.prototype, "track", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Boolean)
], BrowserIdleTracker.prototype, "isStarted", null);
__decorate([
    logActivity(ActivityEventType.SINGLE),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], BrowserIdleTracker.prototype, "dispose", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], BrowserIdleTracker.prototype, "onIdleCallback", null);
__decorate([
    logVerboseDecorator(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], BrowserIdleTracker.prototype, "disconnectContainerOrSchedule", null);
//# sourceMappingURL=browserIdleTracker.js.map